/* Athlete overview — client-side discipline filtering.
 *
 * Mirrors the Magazine blog-grid filter model, simplified (no pagination /
 * splash / layout rebuild — cards just toggle a [hidden] attribute):
 *
 *  - Pills carry data-filter-discipline (handleized).
 *  - Cards carry data-tags (pipe-joined handleized disciplines).
 *  - Multi-select, OR semantics: a card shows if it matches ANY active pill.
 *    Clicking an active pill removes it; clearing the last one shows all.
 *  - URL sync: ?discipline=foo+bar (`+` separated), restored on load.
 *
 * Each section instance is wired independently so multiple overviews can
 * coexist on a page.
 */
(function () {
  function initSection(root) {
    const pillBar = root.querySelector('[data-athlete-pills]');
    const grid = root.querySelector('[data-athlete-grid]');
    if (!grid) return;

    const cards = Array.from(grid.querySelectorAll('[data-athlete-card]'));
    const emptyEl = grid.querySelector('[data-athlete-empty]');
    const active = new Set();

    const reduceMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const OUT_MS = 400; // keep in sync with the card/media transition in CSS

    function cardTags(card) {
      return (card.dataset.tags || '')
        .split('|')
        .map((t) => t.trim())
        .filter(Boolean);
    }

    function matches(card) {
      return active.size === 0 || cardTags(card).some((t) => active.has(t));
    }

    function setEmpty(count) {
      if (emptyEl) emptyEl.hidden = count !== 0;
    }

    // Instant: no transition (initial render + reduced motion).
    function applyInstant() {
      let visible = 0;
      cards.forEach((card) => {
        const show = matches(card);
        card.hidden = !show;
        card.classList.remove('is-filtering-out', 'is-filtering-in');
        if (show) visible += 1;
      });
      setEmpty(visible);
    }

    let outTimer = null;
    let cleanupTimer = null;

    // Everything the exit transition mutates, so it can be undone. Nothing else
    // undoes it: the exit ends by navigating away, and on a bfcache restore the
    // script does NOT re-run — the DOM comes back exactly as it was left.
    const exitClones = []; // fixed-position clones appended to <body>
    const exitHidden = []; // elements we set an inline visibility on

    // Animated: fade/scale current cards out, swap the visible set, FLIP the
    // grid height so the sticky pill bar glides to its new spot, then fade/
    // scale the survivors in.
    function applyAnimated() {
      window.clearTimeout(outTimer);
      window.clearTimeout(cleanupTimer);

      const oldHeight = grid.offsetHeight;
      cards
        .filter((c) => !c.hidden)
        .forEach((c) => c.classList.add('is-filtering-out'));

      outTimer = window.setTimeout(() => {
        let visible = 0;
        cards.forEach((card) => {
          const show = matches(card);
          card.classList.remove('is-filtering-out');
          card.hidden = !show;
          if (show) {
            card.classList.add('is-filtering-in');
            visible += 1;
          }
        });
        setEmpty(visible);

        // FLIP the grid height: lock the old height, force a reflow, then set
        // the new one so the CSS height transition runs.
        const newHeight = grid.offsetHeight;
        grid.style.height = oldHeight + 'px';
        void grid.offsetHeight;
        grid.style.height = newHeight + 'px';

        // Two frames so the entering state paints before we release it.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            cards.forEach((c) => c.classList.remove('is-filtering-in'));
          });
        });

        cleanupTimer = window.setTimeout(() => {
          grid.style.height = '';
        }, 450);
      }, OUT_MS);
    }

    function apply(animate) {
      if (animate && !reduceMotion) applyAnimated();
      else applyInstant();
    }

    // Entrance. The cards render already collapsed via CSS (.is-loading, gated
    // on html.js so there's no flash of full-size cards before a JS collapse),
    // and grow into place when we release it. It fires on scroll-into-view so
    // it also plays when the overview sits at the BOTTOM of a detail page.

    // revealEntrance: apply any active URL filter, then drop .is-loading so the
    // matching cards transition from the collapsed state up to full.
    function revealEntrance() {
      let visible = 0;
      cards.forEach((card) => {
        const show = matches(card);
        card.hidden = !show;
        card.classList.remove('is-filtering-out', 'is-filtering-in');
        if (show) visible += 1;
      });
      setEmpty(visible);
      requestAnimationFrame(() => {
        grid.classList.remove('is-loading');
      });
    }

    // showInstant: no entrance (reduced motion / back-forward) — drop the
    // collapsed state and render the filtered set immediately.
    function showInstant() {
      grid.classList.remove('is-loading');
      applyInstant();
    }

    function syncUrl() {
      const url = new URL(window.location.href);
      if (active.size > 0) {
        url.searchParams.set('discipline', Array.from(active).join('+'));
      } else {
        url.searchParams.delete('discipline');
      }
      window.history.replaceState({}, '', url);
    }

    function setPillState(pill) {
      pill.setAttribute(
        'aria-pressed',
        active.has(pill.dataset.filterDiscipline) ? 'true' : 'false'
      );
    }

    if (pillBar) {
      const pills = Array.from(
        pillBar.querySelectorAll('[data-filter-discipline]')
      );

      // Restore state from ?discipline=foo+bar on load.
      const param = new URL(window.location.href).searchParams.get('discipline');
      if (param) {
        param
          .split(/[+\s]+/)
          .filter(Boolean)
          .forEach((t) => active.add(t));
        pills.forEach(setPillState);
      }

      /* AAS-5568: filtering used to dump you near the foot of the page.
         Nothing here scrolls — hiding cards makes the document shorter, and the
         browser clamps a scroll offset that no longer exists, so you land at
         the new bottom instead of at the results you just asked for. Measured
         on qual: 964 -> 215 with the document going 2915 -> 2166.

         So put the section headline back at the top of the viewport — not the
         grid, and certainly not the pill bar, which sits BELOW the grid here
         (measured: grid at 370, pills at 1873). Landing on the headline gives
         the filtered roster its title back rather than dropping you straight
         into the cards. Asserted twice:
         once now, and once after the exit transition, because the cards are
         only hidden when it ends and that is when the document shrinks and the
         browser clamps again. */
      function scrollOffset() {
        // Clear the sticky header, which would otherwise cover the first row.
        // Same
        // --header-height the rest of the theme spaces off, with the no-JS
        // fallback its other consumers use.
        const h = parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue('--header-height')
        );
        return (Number.isFinite(h) ? h : 40) + 24;
      }

      function returnToTop() {
        const settle = () => {
          const anchor = root.querySelector('.athlete-overview__title') || grid;
          const y = anchor.getBoundingClientRect().top + window.scrollY - scrollOffset();
          const target = Math.max(0, Math.round(y));
          if (Math.abs(window.scrollY - target) < 4) return;
          window.scrollTo({ top: target, behavior: reduceMotion ? 'auto' : 'smooth' });
        };
        settle();
        if (!reduceMotion) setTimeout(settle, OUT_MS + 60);
      }

      pillBar.addEventListener('click', (e) => {
        const pill = e.target.closest('[data-filter-discipline]');
        if (!pill) return;
        const tag = pill.dataset.filterDiscipline;
        if (active.has(tag)) active.delete(tag);
        else active.add(tag);
        setPillState(pill);
        apply(true);
        syncUrl();
        returnToTop();
      });
    }

    // Where the detail-page hero portrait will sit, so the clicked card can be
    // animated to that exact rect before the page loads ("faked" morph). Mobile
    // (<=749) is the full-bleed hero at the very top; otherwise it's the centred
    // 350px card below the header + the intro's top padding.
    function heroTargetRect() {
      const vw = document.documentElement.clientWidth;
      if (vw <= 749) {
        const width = vw;
        return { left: 0, top: 0, width: width, height: width * (455 / 390), radius: 0 };
      }
      const width = 350;
      /* AAS-5568: these three numbers describe the detail hero and have to
         track section-athlete-detail.css:43-52. They drifted, and the morph
         landed visibly wrong — measured on qual at 1440: the clone finished
         350x475 at y 170 while the hero really is 350x408 at y 180.

         height: the hero is `aspect-ratio: 6 / 7`, so 7/6 — it used to be
         475/350 (1.357 against the real 1.167), 67px too tall.

         top: the full header (70) plus the intro's padding-top (--space-22 =
         110). --header-height is NOT the number to use here: it measures
         headerInner, the bar alone, and on the athlete pages it is not even
         set, so the old `|| 60` fallback ran and landed 10px high. Not read
         from the live header either — the overview may be scrolled, so its
         header can be in the collapsed sticky state, while the detail page
         always loads at the top with the full one. */
      return {
        left: (vw - width) / 2,
        top: 70 + 110,
        width: width,
        height: width * (7 / 6),
        radius: 3,
      };
    }

    // Exit transition: the OTHER cards fade + scale out (filter-exit look) while
    // the SELECTED portrait is cloned and animated (move + scale) to where the
    // detail hero will be, then the detail page loads — a "faked" shared-element
    // morph that doesn't depend on cross-document View Transitions.
    grid.addEventListener('click', (e) => {
      const link = e.target.closest('a.athlete-overview__link');
      if (!link || !grid.contains(link)) return;
      // Let reduced motion and new-tab / modified clicks behave normally.
      if (
        reduceMotion ||
        e.defaultPrevented ||
        e.button !== 0 ||
        e.metaKey ||
        e.ctrlKey ||
        e.shiftKey ||
        e.altKey
      ) {
        return;
      }
      const href = link.href;
      if (!href) return;

      const clickedCard = link.closest('[data-athlete-card]');
      const media = clickedCard && clickedCard.querySelector('.athlete-overview__media');
      const img = media && media.querySelector('.athlete-overview__image');
      if (!media || !img) return; // no portrait to morph — let normal nav happen

      e.preventDefault();
      root.classList.add('is-leaving');

      cards.forEach((c) => {
        if (c !== clickedCard && !c.hidden) c.classList.add('is-filtering-out');
      });

      const start = media.getBoundingClientRect();
      const target = heroTargetRect();
      const ease = 'cubic-bezier(.16,1,.3,1)';
      const DUR = 0.5;

      const clone = document.createElement('div');
      clone.setAttribute('aria-hidden', 'true');
      clone.style.cssText =
        'position:fixed;top:0;left:0;margin:0;z-index:50;overflow:hidden;pointer-events:none;' +
        'border-radius:3px;will-change:transform,width,height;' +
        'transition:transform ' + DUR + 's ' + ease + ',width ' + DUR + 's ' + ease +
        ',height ' + DUR + 's ' + ease + ',border-radius ' + DUR + 's ease;' +
        'width:' + start.width + 'px;height:' + start.height + 'px;' +
        'transform:translate(' + start.left + 'px,' + start.top + 'px);';
      const cloneImg = img.cloneNode(true);
      cloneImg.style.cssText = 'width:100%;height:100%;object-fit:cover;display:block;transform:none;';
      clone.appendChild(cloneImg);
      document.body.appendChild(clone);
      exitClones.push(clone);

      media.style.visibility = 'hidden'; // hide original so it doesn't double up
      exitHidden.push(media);

      // Reflow, then animate the clone to the hero rect.
      void clone.offsetWidth;
      clone.style.transform = 'translate(' + target.left + 'px,' + target.top + 'px)';
      clone.style.width = target.width + 'px';
      clone.style.height = target.height + 'px';
      clone.style.borderRadius = target.radius + 'px';

      // Name + country morph: same B3 size on both card and detail, so this is
      // a pure move (no scale, no fade) to the detail hero's id position.
      const meta = clickedCard.querySelector('.athlete-overview__meta');
      if (meta) {
        const vw = document.documentElement.clientWidth;
        const ms = meta.getBoundingClientRect();
        const metaClone = meta.cloneNode(true);
        metaClone.setAttribute('aria-hidden', 'true');
        metaClone.style.cssText =
          'position:fixed;top:0;left:0;margin:0;z-index:50;pointer-events:none;' +
          'white-space:nowrap;text-align:center;';
        document.body.appendChild(metaClone);
        exitClones.push(metaClone);
        meta.style.visibility = 'hidden';
        exitHidden.push(meta);

        const mw = metaClone.offsetWidth;
        const heroGap = vw <= 749 ? 25 : 82; // hero image->name gap (space-5 / 8.2rem)
        const metaTop = target.top + target.height + heroGap;
        // Start centred on the original card meta, then move to the detail id.
        metaClone.style.transform =
          'translate(' + (ms.left + ms.width / 2 - mw / 2) + 'px,' + ms.top + 'px)';
        void metaClone.offsetWidth;
        metaClone.style.transition = 'transform ' + DUR + 's ' + ease;
        metaClone.style.transform = 'translate(' + (vw / 2 - mw / 2) + 'px,' + metaTop + 'px)';
      }

      let navigated = false;
      const go = () => {
        if (navigated) return;
        navigated = true;
        window.location.href = href;
      };
      clone.addEventListener(
        'transitionend',
        (ev) => {
          if (ev.propertyName === 'transform') go();
        },
        { once: true }
      );
      window.setTimeout(go, DUR * 1000 + 150); // fallback if transitionend doesn't fire
    });

    // Undo the exit transition. Required on a bfcache restore (back from a
    // detail page): the page is resumed, not re-executed, so the DOM returns
    // mid-exit — the fixed-position portrait clone still floating over an empty
    // page, every other card left at opacity 0 by .is-filtering-out, the
    // clicked card's media/meta still visibility:hidden, and .is-leaving still
    // suppressing pointer events on the whole section. The grid keeps its
    // layout box throughout, which is why the page still scrolls.
    function resetExitState() {
      window.clearTimeout(outTimer);
      window.clearTimeout(cleanupTimer);
      exitClones.forEach((el) => el.remove());
      exitClones.length = 0;
      exitHidden.forEach((el) => {
        el.style.visibility = '';
      });
      exitHidden.length = 0;
      root.classList.remove('is-leaving');
      cards.forEach((c) => c.classList.remove('is-filtering-out', 'is-filtering-in'));
      grid.style.height = '';
    }

    // `persisted` is the bfcache case. The JS heap survives with the DOM, so
    // `active` still holds the pill selection and showInstant() re-renders the
    // same filtered set the visitor left behind.
    window.addEventListener('pageshow', (e) => {
      if (!e.persisted) return;
      resetExitState();
      showInstant();
    });

    // Skip the entrance animation on back/forward navigation — returning to the
    // overview should show the grid as it was, not re-fade it in.
    const navEntry = performance.getEntriesByType('navigation')[0];
    const isBackForward = navEntry && navEntry.type === 'back_forward';

    // Last-resort safety net. The entrance is the ONLY thing that drops
    // .is-loading, so anything that stops it firing leaves the page looking
    // empty. Never let that be the final state.
    const entranceFallback = window.setTimeout(() => {
      if (grid.classList.contains('is-loading')) showInstant();
    }, 1500);

    if (reduceMotion || isBackForward) {
      window.clearTimeout(entranceFallback);
      showInstant();
    } else if ('IntersectionObserver' in window) {
      // Release the collapsed cards when the grid first scrolls into view — so
      // the grow-in plays both on the overview page (in view on load → fires
      // immediately) and when scrolling down to the overview on a detail page.
      //
      // threshold MUST stay 0. intersectionRatio is measured against the
      // TARGET's area, not the viewport's, so a grid taller than 1/threshold
      // viewports can never reach it. At the old 0.1 the 85-athlete roster
      // (~29 rows, ~14600px) topped out around 0.06, the callback never fired,
      // .is-loading was never dropped and every card stayed at opacity 0 —
      // invisible but still occupying layout, so they were clickable. Filtering
      // "fixed" it only because the shrunken grid finally cleared 0.1.
      // The negative bottom margin keeps the original "a bit into view" feel
      // without tying it to the grid's height.
      const io = new IntersectionObserver(
        (entries, obs) => {
          if (entries.some((e) => e.isIntersecting)) {
            obs.disconnect();
            window.clearTimeout(entranceFallback);
            revealEntrance();
          }
        },
        { threshold: 0, rootMargin: '0px 0px -120px 0px' }
      );
      io.observe(grid);
    } else {
      window.clearTimeout(entranceFallback);
      revealEntrance();
    }
  }

  document.querySelectorAll('.athlete-overview').forEach(initSection);
})();
